/**
 * Helpers “génériques” pour le calcul des stats.
 */

import type { FolderFilesCountQuery } from "src/core/gql/graphql";
import type { DashboardCounts } from "src/core/hooks/folders-list/folder-risk-statistics/types";
import { formatIsoToDate } from "src/core/utils/date";

export const toPercent2Decimals = (ratio: number): number => Number((ratio * 100).toFixed(2));

export const safePercent2Decimals = (count: number, total: number): number => {
  if (total <= 0) return 0;
  return toPercent2Decimals(count / total);
};

export const safeTotalCount = (pageInfo?: { totalCount?: number | null } | null): number =>
  pageInfo?.totalCount ?? 0;

export const extractDashboardCounts = (
  data: FolderFilesCountQuery | undefined
): DashboardCounts => {
  if (!data) {
    return {
      draft: 0,
      validationPending: 0,
      validationPendingHr: 0,
      renewal: 0,
      validated: 0,
    };
  }

  return {
    draft: safeTotalCount(data.draft?.pageInfo),
    validationPending: safeTotalCount(data.validationPending?.pageInfo),
    validationPendingHr: safeTotalCount(data.validationPendingHr?.pageInfo),
    renewal: safeTotalCount(data.renewal?.pageInfo),
    validated: safeTotalCount(data.validated?.pageInfo),
  };
};

export const computeExtractionDate = (): string => formatIsoToDate(new Date().toISOString());

/**
 * Construit un nom affichable (personne vs entreprise).
 */
export const computeCustomerDisplayName = (customer: {
  isPerson?: boolean | null;
  firstName?: { stringValue?: string | null } | null;
  lastName?: { stringValue?: string | null } | null;
  name?: { stringValue?: string | null } | null;
}): { isPerson: boolean; name: string | null | undefined } => {
  const isPerson = customer.isPerson ?? false;

  if (isPerson) {
    const fullName = [customer.firstName?.stringValue, customer.lastName?.stringValue]
      .filter(Boolean)
      .join(" ");
    return { isPerson, name: fullName };
  }

  return { isPerson, name: customer.name?.stringValue };
};

export const computeOwnerDisplayName = (
  owner?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null
): string | null => {
  if (!owner) return null;
  const first = owner.firstName ?? "";
  const last = owner.lastName ?? "";
  const display = `${first} ${last}`.trim();
  return display.length > 0 ? display : null;
};
